import {
  boolean,
  email,
  type InferOutput,
  integer,
  intersect,
  maxLength,
  maxValue,
  minLength,
  minValue,
  number,
  object,
  pipe,
  string,
  trim,
} from "valibot";

export const EmailPipeline = pipe(
  string(),
  trim(),
  minLength(1, "Email is required"),
  maxLength(254, "Email must be 254 characters or less"),
  email("Invalid email address format"),
);

export const EmailSchema = object({
  email: EmailPipeline,
});

export const LoginSchema = intersect([
  EmailSchema,
  object({
    password: pipe(string(), minLength(1, "Password is required")),
  }),
]);
export type LoginSchemaType = InferOutput<typeof LoginSchema>;

export const NoteSchema = object({
  text: pipe(string(), trim(), minLength(1, "Text is required")),
});

export type NoteSchemaType = InferOutput<typeof NoteSchema>;

export const CampaignNameSchema = object({
  name: pipe(
    string(),
    trim(),
    minLength(1, "Name is required"),
    maxLength(50, "Campaign name can't be more than 50 characters long"),
  ),
});

export const SearchCampaignAudienceSizeSchema = object({
  size: pipe(
    number("Invalid value"),
    minValue(1, "Limit is required"),
    maxValue(1000, "Limit is 1000"),
  ),
});

export const ExtraCampaignSettingsSchema = object({
  exclude_active_targets: boolean(),
  exclude_previous_targets: boolean(),
  no_pfp: boolean(),
});

export type ExtraCampaignSettingsSchemaType = InferOutput<
  typeof ExtraCampaignSettingsSchema
>;

export const DefaultCampaignSettingsSchema = object({
  ...CampaignNameSchema.entries,
  extraSettings: ExtraCampaignSettingsSchema,
});

export type DefaultCampaignSettingsSchemaType = InferOutput<
  typeof DefaultCampaignSettingsSchema
>;

export const DripCampaignSettingsSchema = object({
  ...CampaignNameSchema.entries,
  extraSettings: ExtraCampaignSettingsSchema,
});

export const SearchCampaignSettingsSchema = object({
  ...CampaignNameSchema.entries,
  ...SearchCampaignAudienceSizeSchema.entries,
  extraSettings: ExtraCampaignSettingsSchema,
});

export const NavigatorCampaignSettingsSchema = object({
  ...CampaignNameSchema.entries,
  extraSettings: ExtraCampaignSettingsSchema,
  size: pipe(
    number("Invalid value"),
    minValue(1, "Limit is required"),
    maxValue(2500, "Limit is 2500"),
  ),
});

export const WorkspaceNameSchema = object({
  name: pipe(
    string(),
    trim(),
    minLength(1, "Workspace name is required"),
    maxLength(30, "Workspace name can't be more than 30 characters long"),
  ),
});

export const AssignSeatsSchema = object({
  seats: pipe(number("Seats to assign must be a number"), integer()),
});
export type AssignSeatsSchemaType = InferOutput<typeof AssignSeatsSchema>;

export const CustomProxySchema = object({
  name: pipe(string(), trim(), minLength(1, "Name is required")),
  protocol: pipe(string(), trim(), minLength(1, "Protocol is required")),
  address: pipe(string(), trim(), minLength(1, "Address is required")),
  username: pipe(string(), trim()),
  password: pipe(string(), trim()),
});

export type CustomProxySchemaType = InferOutput<typeof CustomProxySchema>;

export const CancelSubscriptionFormSchema = object({
  reason: pipe(
    string(),
    minLength(3, "Feedback must be at least 3 characters"),
    maxLength(2000, "Feedback can't be more than 2000 characters"),
  ),
});

export type CancelSubscriptionFormSchemaType = InferOutput<
  typeof CancelSubscriptionFormSchema
>;
